/**
 * Track chips row (SPEC §6.2): drums / bass / chords / lead. Each chip selects
 * the track shown in the grid and carries M / S toggles, a small volume
 * slider (−24..6 dB) and note counts split amber (human) / teal (agent). An
 * "agent" badge shows while a track holds agent notes the human has not
 * touched since (counts are diffed on every store change).
 */
import type { Store } from '../state/store-api';
import type { Session, TrackId } from '../types';
import { TRACK_IDS, VOLUME_MAX_DB, VOLUME_MIN_DB, clamp } from '../types';
import { h, injectStyle, setFlag } from './dom';

/** Dependencies for `mountTracks()`. */
export interface TracksDeps {
  store: Store;
}

/** Mounted chips row: root element + dispose. */
export interface TracksHandle {
  el: HTMLElement;
  dispose(): void;
}

const NAME: Record<TrackId, string> = { drums: 'Drums', bass: 'Bass', chords: 'Chords', lead: 'Lead' };

/** Human / agent counts for a track. */
export function trackCounts(s: Session, id: TrackId): { human: number; agent: number } {
  const items = id === 'drums' ? s.tracks.drums.hits : s.tracks[id].notes;
  let human = 0;
  let agent = 0;
  for (const x of items) x.by === 'human' ? human++ : agent++;
  return { human, agent };
}

const CSS = `
.bm-chips{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.5rem;min-width:0}
@media (max-width:600px){.bm-chips{grid-template-columns:repeat(2,minmax(0,1fr))}}
.bm-chip{display:flex;flex-direction:column;gap:.35rem;padding:.5rem .6rem;border:1px solid var(--border);border-radius:var(--radius-m);
  background:var(--surface-1);min-width:0;transition:border-color .12s var(--ease)}
.bm-chip[data-selected]{border-color:var(--fg-faint);background:var(--surface-2)}
.bm-chip[data-muted="true"]{opacity:.65}
.bm-chip__top{display:flex;align-items:center;gap:.35rem;min-width:0}
.bm-chip__select{flex:1 1 auto;min-width:0;display:flex;align-items:center;gap:.4rem;text-align:left;padding:.15rem .3rem;border:0;background:none;
  border-radius:var(--radius-s);cursor:pointer;color:var(--fg)}
.bm-chip__select:hover{background:var(--surface-3)}
.bm-chip__select[aria-pressed="true"]{font-weight:600}
.bm-chip__name{white-space:nowrap}
.bm-chip__counts{font-size:.75rem;font-variant-numeric:tabular-nums;color:var(--fg-muted);white-space:nowrap}
.bm-chip__counts [data-count-human]{color:var(--human)}
.bm-chip__counts [data-count-agent]{color:var(--agent)}
.bm-chip__badge{font-size:.62rem;letter-spacing:.06em;text-transform:uppercase;color:var(--agent-fg);background:var(--agent);border-radius:4px;padding:.05em .35em;font-weight:700}
.bm-chip__toggles{display:flex;gap:.25rem}
.bm-chip__toggle{width:1.7rem;min-height:1.7rem;padding:0;font-size:.75rem;font-weight:700;border-radius:var(--radius-s)}
.bm-chip__toggle[data-kind="mute"][aria-pressed="true"]{background:var(--danger-soft);border-color:var(--danger);color:var(--fg)}
.bm-chip__toggle[data-kind="solo"][aria-pressed="true"]{background:var(--human-soft);border-color:var(--human);color:var(--fg)}
.bm-chip__vol{display:flex;align-items:center;gap:.35rem;font-size:.7rem;color:var(--fg-muted);min-width:0}
.bm-chip__vol input{flex:1 1 auto;min-width:0;height:1rem}
.bm-chip__vol output{font-variant-numeric:tabular-nums;min-width:3.2em;text-align:right}
`;

/** Mount the four track chips into `container`. */
export function mountTracks(container: HTMLElement, deps: TracksDeps): TracksHandle {
  injectStyle('bm-tracks-css', CSS);
  const { store } = deps;

  const fresh: Record<TrackId, boolean> = { drums: false, bass: false, chords: false, lead: false };
  let prev: Record<TrackId, { human: number; agent: number }> = { drums: { human: 0, agent: 0 }, bass: { human: 0, agent: 0 }, chords: { human: 0, agent: 0 }, lead: { human: 0, agent: 0 } };

  const seed = (s: Session) => {
    for (const id of TRACK_IDS) {
      const c = trackCounts(s, id);
      prev[id] = c;
      fresh[id] = c.agent > 0 && c.human === 0;
    }
  };
  seed(store.get());

  const chips = {} as Record<
    TrackId,
    {
      root: HTMLElement;
      select: HTMLButtonElement;
      human: HTMLElement;
      agent: HTMLElement;
      badge: HTMLElement;
      mute: HTMLButtonElement;
      solo: HTMLButtonElement;
      volume: HTMLInputElement;
      volOut: HTMLOutputElement;
    }
  >;

  const el = h('div', { class: 'bm-chips', role: 'group', 'aria-label': 'Tracks' });

  for (const id of TRACK_IDS) {
    const human = h('span', { 'data-count-human': true, title: 'by you' }, '0');
    const agent = h('span', { 'data-count-agent': true, title: 'by the agent' }, '0');
    const badge = h('span', { class: 'bm-chip__badge', 'data-badge': true, hidden: true }, 'agent');
    const select = h(
      'button',
      { type: 'button', class: 'bm-chip__select', 'data-select': true, 'aria-pressed': 'false' },
      h('span', { class: 'bm-chip__name' }, NAME[id]),
      h('span', { class: 'bm-chip__counts', 'aria-hidden': 'true' }, human, ' · ', agent),
      badge,
    );
    select.addEventListener('click', () => {
      if (store.get().ui.selectedTrack === id) return;
      store.apply({ type: 'select', track: id }, { by: 'human', label: `selected ${id}` });
      store.logHuman({ kind: 'select', detail: `selected ${id}` });
    });

    const mute = h(
      'button',
      { type: 'button', class: 'bm-btn bm-chip__toggle', 'data-mute': true, 'data-kind': 'mute', 'aria-pressed': 'false', 'aria-label': `Mute ${id}` },
      'M',
    );
    mute.addEventListener('click', () => {
      const muted = !store.get().tracks[id].muted;
      store.apply({ type: 'setTrack', track: id, muted }, { by: 'human', label: `${muted ? 'muted' : 'unmuted'} ${id}` });
      store.logHuman({ kind: muted ? 'mute' : 'unmute', detail: `${muted ? 'muted' : 'unmuted'} ${id}` });
    });
    const solo = h(
      'button',
      { type: 'button', class: 'bm-btn bm-chip__toggle', 'data-solo': true, 'data-kind': 'solo', 'aria-pressed': 'false', 'aria-label': `Solo ${id}` },
      'S',
    );
    solo.addEventListener('click', () => {
      const on = !store.get().tracks[id].solo;
      store.apply({ type: 'setTrack', track: id, solo: on }, { by: 'human', label: `${on ? 'soloed' : 'unsoloed'} ${id}` });
      store.logHuman({ kind: on ? 'solo' : 'unsolo', detail: `${on ? 'soloed' : 'unsoloed'} ${id}` });
    });

    const volOut = h('output', { 'aria-hidden': 'true' }) as HTMLOutputElement;
    const volume = h('input', {
      type: 'range',
      class: 'bm-range',
      min: VOLUME_MIN_DB,
      max: VOLUME_MAX_DB,
      step: 1,
      'data-volume': true,
      'aria-label': `${NAME[id]} volume (dB)`,
    }) as HTMLInputElement;
    volume.addEventListener('input', () => {
      volOut.value = `${volume.value} dB`;
    });
    volume.addEventListener('change', () => {
      const v = clamp(Math.round(Number(volume.value)), VOLUME_MIN_DB, VOLUME_MAX_DB);
      volume.value = String(v);
      volOut.value = `${v} dB`;
      if (v === store.get().tracks[id].volume) return;
      store.apply({ type: 'setTrack', track: id, volume: v }, { by: 'human', label: `set ${id} volume ${v} dB` });
      store.logHuman({ kind: 'volume', detail: `set ${id} volume ${v} dB` });
    });

    const root = h(
      'div',
      { class: 'bm-chip', dataset: { track: id } },
      h('div', { class: 'bm-chip__top' }, select, h('div', { class: 'bm-chip__toggles' }, mute, solo)),
      h('label', { class: 'bm-chip__vol' }, volume, volOut),
    );
    chips[id] = { root, select, human, agent, badge, mute, solo, volume, volOut };
    el.append(root);
  }

  const render = (s: Session) => {
    for (const id of TRACK_IDS) {
      const c = chips[id];
      const t = s.tracks[id];
      const counts = trackCounts(s, id);
      c.human.textContent = String(counts.human);
      c.agent.textContent = String(counts.agent);
      c.select.setAttribute(
        'aria-label',
        `${NAME[id]}: ${counts.human} by you, ${counts.agent} by the agent${s.ui.selectedTrack === id ? ', selected' : ''}`,
      );
      c.select.setAttribute('aria-pressed', String(s.ui.selectedTrack === id));
      setFlag(c.root, 'data-selected', s.ui.selectedTrack === id);
      c.root.dataset.muted = String(t.muted);
      c.root.dataset.solo = String(t.solo);
      c.mute.setAttribute('aria-pressed', String(t.muted));
      c.solo.setAttribute('aria-pressed', String(t.solo));
      if (document.activeElement !== c.volume) {
        c.volume.value = String(t.volume);
        c.volOut.value = `${t.volume} dB`;
      }
      c.badge.hidden = !fresh[id];
    }
  };
  render(store.get());

  const unsub = store.subscribe((s, meta) => {
    // Counts render from whatever is sounding (transient audition / live recording included),
    // but "fresh" is diffed against committed state only.
    if (meta.source === 'load') seed(s);
    else if (meta.source !== 'transient') {
      for (const id of TRACK_IDS) {
        const now = trackCounts(s, id);
        const was = prev[id];
        if (now.agent > was.agent) fresh[id] = true;
        if (now.human !== was.human) fresh[id] = false;
        if (now.agent === 0) fresh[id] = false;
        prev[id] = now;
      }
    }
    render(s);
  });

  container.append(el);
  return {
    el,
    dispose() {
      unsub();
      el.remove();
    },
  };
}
